import { normalizeRegion, regionWeight, type RegionShape } from '../effects/region-shape';
import type { OverlayData, OverlayTrigger } from '../types';

/**
 * Trigger logic shared by overlays and by triggered focus / haze regions, kept free of
 * DOM and engine objects so it is unit-testable.
 *
 * Triggers come in two kinds:
 * - Conditions (region, waypoint, between waypoints, progress) are re-evaluated every frame. The item
 *   shows while a condition holds and hides when it stops holding, unless the trigger
 *   opts out of hiding, in which case the item stays until dismissed.
 * - Events (scene start, hotspot click, manual show) latch the item on until it is
 *   dismissed, hidden through the API, or auto-hides. A hotspot trigger's `action` can
 *   instead switch the item off, or flip it (on / off switch); `hotspotToggles` makes
 *   flipping the default for triggers that don't say.
 *
 * Dismissing suppresses the item while its condition still holds; leaving and
 * re-entering re-arms it. Auto-hide behaves the same way.
 */
export interface OverlayFrame {
    /** Camera position in editor scene coordinates (the region editor's convention). */
    cameraScenePosition: { x: number; y: number; z: number };
    /** Tour progress 0..1. */
    progress: number;
    waypointIndex: number;
    /**
     * Where the tour is along its waypoints, as a fractional 0-based index (2.5 = halfway from
     * the third waypoint to the fourth), or -1 when the tour isn't driving the camera.
     */
    waypointPosition?: number;
    /** The tour loops: after the last waypoint it runs on to the first. */
    tourLoops?: boolean;
    timeMs: number;
}

/** Anything the controller can drive: an overlay, a focus region, a haze region. */
export interface TriggeredItem {
    id: string;
    enabled?: boolean;
    triggers?: OverlayTrigger[];
    autoHideMs?: number;
}

export interface TriggerListener<T extends TriggeredItem> {
    /** Return false when nothing could be shown (e.g. no content); the item then stays hidden. */
    show(item: T): boolean | void;
    hide(item: T): void;
    /**
     * A visible item's data changed (the editor replaces the set on each keystroke). Return
     * true when the change was applied in place; otherwise, or when this is not implemented,
     * the controller hides the old item and re-shows the new one.
     */
    update?(previous: T, next: T): boolean | void;
}
export type OverlayListener = TriggerListener<OverlayData>;

export interface TriggerControllerOptions {
    /**
     * Hotspot triggers without an `action` toggle (a click on a shown item hides it) instead of
     * showing. Focus / haze regions and annotation layers toggle; overlays show.
     */
    hotspotToggles?: boolean;
}

/** Structural equality for authored item data (plain JSON). */
function sameData(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return false;
    }
}

const isCondition = (t: OverlayTrigger) => t.type === 'region' || t.type === 'waypoint' || t.type === 'betweenWaypoints' || t.type === 'progress';

/**
 * The tour at `position` (a fractional waypoint index) is between waypoints `from` and `to`,
 * both included. A looping tour wraps when `from` comes after `to`; otherwise the order of
 * the two doesn't matter.
 */
export function isBetweenWaypoints(position: number, from: number, to: number, loops: boolean): boolean {
    const eps = 1e-6; // a waypoint's own progress can land a hair short of its index
    if (from <= to) return position >= from - eps && position <= to + eps;
    if (loops) return position >= from - eps || position <= to + eps;
    return position >= to - eps && position <= from + eps;
}

export function regionTriggerShape(trigger: Extract<OverlayTrigger, { type: 'region' }>): RegionShape {
    return normalizeRegion(trigger);
}

export class TriggerController<T extends TriggeredItem = TriggeredItem> {
    private items: T[] = [];
    private visible = new Set<string>();
    private shownAt = new Map<string, number>();
    private latched = new Set<string>();
    private suppressed = new Set<string>();
    private conditionWas = new Map<string, boolean>();
    private startedAt: number | null = null;
    private firedStart = new Set<string>();
    private lastFrame: OverlayFrame | null = null;

    constructor(private listener: TriggerListener<T>, private options: TriggerControllerOptions = {}) {}

    setItems(items: T[] | undefined): void {
        const previous = new Map(this.items.map((o) => [o.id, o] as const));
        const next = Array.isArray(items) ? items.filter((o) => o && typeof o.id === 'string') : [];
        const ids = new Set(next.map((o) => o.id));
        // Visible items that are gone are hidden.
        for (const item of this.items) {
            if (this.visible.has(item.id) && !ids.has(item.id)) this.forceHidden(item);
        }
        this.items = next;
        // Per-item state survives for ids that are still present (the editor calls this on
        // each keystroke): a manual / hotspot show stays latched, a dismissal stays suppressed,
        // a fired scene-start does not fire again, condition edges are not re-detected, and the
        // auto-hide clock keeps running.
        for (const set of [this.latched, this.suppressed, this.firedStart]) for (const id of Array.from(set)) if (!ids.has(id)) set.delete(id);
        for (const id of Array.from(this.conditionWas.keys())) if (!ids.has(id)) this.conditionWas.delete(id);
        // A visible item whose data is unchanged stays up untouched. A changed one is updated
        // in place when the listener can, otherwise hidden here and re-shown from the new data
        // by update() below if its trigger still holds.
        for (const item of next) {
            const prev = previous.get(item.id);
            if (!prev || !this.visible.has(item.id) || sameData(prev, item)) continue;
            if (this.listener.update?.(prev, item) !== true) this.forceHidden(prev);
        }
        if (this.lastFrame) this.update(this.lastFrame);
    }

    private forceHidden(item: T): void {
        this.visible.delete(item.id);
        this.shownAt.delete(item.id);
        this.listener.hide(item);
    }

    getItems(): T[] {
        return this.items;
    }

    /** Call once the scene has loaded; scene-start triggers count from here. */
    start(timeMs: number): void {
        if (this.startedAt === null) this.startedAt = timeMs;
    }

    isVisible(id: string): boolean {
        return this.visible.has(id);
    }

    getVisibleIds(): string[] {
        return this.items.filter((o) => this.visible.has(o.id)).map((o) => o.id);
    }

    update(frame: OverlayFrame): void {
        this.lastFrame = frame;
        for (const item of this.items) {
            if (item.enabled === false) {
                this.setVisible(item, false, frame.timeMs);
                continue;
            }
            const triggers = Array.isArray(item.triggers) ? item.triggers : [];
            let condition = false;
            let latchOnEnter = false;
            for (const trigger of triggers) {
                if (!isCondition(trigger)) {
                    if (trigger.type === 'sceneStart' && this.startedAt !== null && !this.firedStart.has(item.id)) {
                        const delay = Math.max(0, Number(trigger.delayMs) || 0);
                        if (frame.timeMs - this.startedAt >= delay) {
                            this.firedStart.add(item.id);
                            this.latched.add(item.id);
                        }
                    }
                    continue;
                }
                if (!this.evaluate(trigger, frame)) continue;
                condition = true;
                if ((trigger.type === 'region' && trigger.hideOnExit === false)
                    || (trigger.type === 'waypoint' && trigger.hideOnLeave === false)) latchOnEnter = true;
            }
            const was = this.conditionWas.get(item.id) ?? false;
            if (condition && !was) {
                this.suppressed.delete(item.id);
                if (latchOnEnter) this.latched.add(item.id);
            } else if (!condition && was) {
                this.suppressed.delete(item.id);
            }
            this.conditionWas.set(item.id, condition);

            let target = this.latched.has(item.id) || (condition && !this.suppressed.has(item.id));
            const autoHide = Number(item.autoHideMs);
            const since = this.shownAt.get(item.id);
            if (target && this.visible.has(item.id) && autoHide > 0 && since !== undefined && frame.timeMs - since >= autoHide) {
                this.latched.delete(item.id);
                this.suppressed.add(item.id);
                target = false;
            }
            this.setVisible(item, target, frame.timeMs);
        }
    }

    /** A hotspot was clicked: show, hide or flip every item listening for it, as its trigger says. */
    trigger(kind: 'hotspot', id: string): void {
        for (const item of this.items) {
            if (item.enabled === false) continue;
            const trigger = (item.triggers || []).find((t) => t.type === 'hotspot' && t.hotspotId === id);
            if (trigger?.type !== 'hotspot') continue;
            const action = trigger.action ?? (this.options.hotspotToggles ? 'toggle' : 'on');
            if (action === 'off' || (action === 'toggle' && this.visible.has(item.id))) this.hide(item.id);
            else this.show(item.id);
        }
    }

    show(id: string): boolean {
        const item = this.find(id);
        if (!item) return false;
        this.latched.add(id);
        this.suppressed.delete(id);
        // Showing an item that is already up restarts its auto-hide clock.
        if (this.visible.has(id)) this.shownAt.set(id, this.now());
        this.setVisible(item, true, this.now());
        return true;
    }

    hide(id: string): boolean {
        const item = this.find(id);
        if (!item) return false;
        this.latched.delete(id);
        if (this.conditionWas.get(id)) this.suppressed.add(id);
        this.setVisible(item, false, this.now());
        return true;
    }

    /** The visitor closed it: same as hide(), kept separate for analytics and intent. */
    dismiss(id: string): boolean {
        return this.hide(id);
    }

    destroy(): void {
        for (const item of this.items) if (this.visible.has(item.id)) this.listener.hide(item);
        this.items = [];
        this.visible.clear();
    }

    private now(): number {
        return this.lastFrame?.timeMs ?? (typeof performance !== 'undefined' ? performance.now() : Date.now());
    }

    private find(id: string): T | undefined {
        return this.items.find((o) => o.id === id);
    }

    private evaluate(trigger: OverlayTrigger, frame: OverlayFrame): boolean {
        switch (trigger.type) {
            case 'region':
                // Anywhere inside the authored shape counts, so the trigger fires at the boundary
                // the creator sees in the editor rather than partway through the feather band.
                return regionWeight(regionTriggerShape(trigger), frame.cameraScenePosition) > 0;
            case 'waypoint':
                return Number.isInteger(trigger.index) && frame.waypointIndex === trigger.index;
            case 'betweenWaypoints': {
                const position = frame.waypointPosition ?? -1;
                if (!Number.isFinite(position) || position < 0) return false;
                if (!Number.isInteger(trigger.from) || !Number.isInteger(trigger.to)) return false;
                return isBetweenWaypoints(position, trigger.from, trigger.to, frame.tourLoops === true);
            }
            case 'progress': {
                const p = frame.progress * 100;
                const start = Number.isFinite(trigger.start) ? trigger.start : 0;
                const end = Number.isFinite(trigger.end) ? trigger.end : 100;
                return p >= Math.min(start, end) && p <= Math.max(start, end);
            }
            default:
                return false;
        }
    }

    private setVisible(item: T, target: boolean, timeMs: number): void {
        const current = this.visible.has(item.id);
        if (target === current) return;
        if (target) {
            // Mark visible before notifying so listeners that consult isVisible() (region
            // effects rebuilding from the active set) see the new state; roll back on veto.
            this.visible.add(item.id);
            this.shownAt.set(item.id, timeMs);
            if (this.listener.show(item) === false) {
                this.visible.delete(item.id);
                this.shownAt.delete(item.id);
            }
        } else {
            this.visible.delete(item.id);
            this.shownAt.delete(item.id);
            this.listener.hide(item);
        }
    }
}

/** Overlay panels: the original, non-toggling flavour of the trigger controller. */
export class OverlayController extends TriggerController<OverlayData> {
    setOverlays(overlays: OverlayData[] | undefined): void {
        this.setItems(overlays);
    }
    getOverlays(): OverlayData[] {
        return this.getItems();
    }
}
